import { useQuery } from '@tanstack/react-query';

import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';
import type { Period } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { sanitizeTagValue } from '~/utils/instrumentation';

const log = createLogger('usePeriodQuery');

export const QUERY_KEY = 'periodById';

export type GetPeriodByIdRequest = {
  periodId: string;
  includeStudents?: boolean;
};

export type GetPeriodByIdResponse = {
  period: Period;
};

export function usePeriodGetByIdQuery({
  periodId,
  includeStudents = true,
}: {
  periodId: string | undefined;
  includeStudents?: boolean;
}) {
  const httpClient = useHttpClient();

  const { data, error, status } = useQuery<null | GetPeriodByIdResponse>({
    queryKey: [QUERY_KEY, periodId, includeStudents],
    queryFn: async () => {
      if (!periodId) {
        return null;
      }
      log.debug('Fetching period by id', { periodId });
      const response = await httpClient.post<GetPeriodByIdResponse>('/period/getById', {
        periodId,
        includeStudents,
      });
      log.info('Fetched period successfully', {
        periodId: response.period.id,
        periodName: sanitizeTagValue(response.period.name),
        periodShortName: sanitizeTagValue(response.period.shortName),
      });
      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch period by id', failureCount, error, log }),
    enabled: httpClient.isAuthenticated && Boolean(periodId),
  });

  return { data, error, status };
}
